import { observer } from 'mobx-react-lite'
import { Button } from 'app/components/ui/button'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import { paletteGroups, type PaletteItem } from 'app/lib/workflows/palette'
import { useAuthStore } from 'app/stores/StoreContext'

type BlockPickerProps = {
  screen: { x: number; y: number }
  /** Edit task is offered only where it has a task to edit. */
  onTaskPath: boolean
  onPick: (item: PaletteItem) => void
  onClose: () => void
}

const WIDTH = 224
const MAX_HEIGHT = 340

/** Floating menu of insertable blocks, anchored near a screen point. Used by both the
 *  connector "+" and the drag-off-a-handle interactions. */
export const BlockPicker = observer(function BlockPicker({ screen, onTaskPath, onPick, onClose }: BlockPickerProps) {
  const { isSuper } = useAuthStore()
  const paperworkEnabled = !!useFeatureToggle(`paperwork`)
  // Triggers are entry-only, so they can't be inserted mid-flow.
  const insertableGroups = paletteGroups(useListCopy()).filter((group) => group.title !== `Triggers`)

  useEscapeListener(onClose)

  // Keep the menu on-screen.
  const left = Math.min(screen.x, window.innerWidth - WIDTH - 8)
  const top = Math.min(screen.y, window.innerHeight - MAX_HEIGHT - 8)

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 overflow-y-auto rounded-lg border border-default bg-surface-raised p-1 shadow-xl"
        style={{ left, top, width: WIDTH, maxHeight: MAX_HEIGHT }}
      >
        {insertableGroups.map((group) => (
          <div key={group.title}>
            <div className="px-2 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-minimal uppercase">
              {group.title}
            </div>
            {group.items
              .filter(
                (item) =>
                  (isSuper || !item.superOnly) &&
                  (onTaskPath || item.kind !== `editTask`) &&
                  (paperworkEnabled || item.kind !== `sendPaperwork`),
              )
              .map((item) => (
                <Button key={item.label} variant="ghost" size="row" onClick={() => onPick(item)} className="min-w-0">
                  <span className={cn(`flex size-6 shrink-0 items-center justify-center rounded-md`, item.chip)}>
                    <item.icon size={14} weight="bold" />
                  </span>
                  <span className="truncate text-xs font-medium text-default">{item.label}</span>
                </Button>
              ))}
          </div>
        ))}
      </div>
    </>
  )
})
